<template>
  <div v-if="shouldShow && !isCollapsed">
    <div class="tw-bg-indigo-50 tw-rounded-tl-lg tw-rounded-tr-lg tw-relative tw-overflow-hidden">
      <!-- Left decorative SVG -->
      <div class="tw-absolute -tw-left-3 -tw-top-3">
        <svg
          width="187"
          height="187"
          viewBox="0 0 187 187"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M93.5 0C117.333 0 136.654 41.8614 136.654 93.5C136.654 145.139 117.333 187 93.5 187C69.6668 187 50.3462 145.139 50.3462 93.5C50.3462 41.8614 69.6668 0 93.5 0Z"
            fill="#6A54F4"
          />
          <path
            d="M187 93.5C187 117.333 145.139 136.654 93.5 136.654C41.8614 136.654 0 117.333 0 93.5C0 69.6668 41.8614 50.3462 93.5 50.3462C145.139 50.3462 187 69.6668 187 93.5Z"
            fill="#6A54F4"
          />
        </svg>
      </div>

      <!-- Right decorative SVG -->
      <div class="tw-absolute -tw-right-9 -tw-top-20">
        <svg
          width="314"
          height="261"
          viewBox="0 0 314 261"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M8.59512 158.177L160.892 148.98L279.273 245.234L126.976 254.431L8.59512 158.177Z"
            fill="#FFC800"
          />
          <path
            d="M25.5531 105.452L177.85 96.254L296.231 192.508L143.934 201.706L25.5531 105.452Z"
            fill="#FFC800"
          />
          <path
            d="M42.5109 52.7258L194.808 43.5282L313.189 139.782L160.892 148.98L42.5109 52.7258Z"
            fill="#FFC800"
          />
        </svg>
      </div>

      <!-- Main content -->
      <div class="tw-relative tw-z-10 tw-pt-3 tw-pb-5 tw-px-6">
        <WdsStack direction-column align-center>
          <div class="tw-pb-1">
            <WdsButtonGhost @click="toggleCollapsed">
              <WdsStack align-center gap-xs>
                <WdsText class="tw-text-sm">{{ $t('Hide') }}</WdsText>
                <WdsIcon name="chevron-up" :size="16" />
              </WdsStack>
            </WdsButtonGhost>
          </div>

          <!-- Welcome message -->
          <WdsStack direction-column align-center class="tw-pb-3">
            <WdsHeading type="display" class="tw-text-indigo-500 tw-mb-2">
              {{ $t('Welcome to Worksome!') }}
            </WdsHeading>
            <WdsText>
              <div class="tw-text-lg tw-text-center tw-max-w-md">
                {{
                  $t(
                    'Complete your account to get access to full functionality and start working as soon as possible!',
                  )
                }}
              </div>
            </WdsText>
          </WdsStack>

          <!-- CTA Button -->
          <div v-if="!isFreelancerOnboarded && !loadingAccount">
            <a :href="laravelNamedRoute(namedRoutes.frontend.user.profile.edit)">
              <WdsButton primary lg>
                <WdsStack align-center gap-sm>
                  {{ $t('Complete your account') }}
                  <WdsIcon name="arrow-right" :size="16" />
                </WdsStack>
              </WdsButton>
            </a>
          </div>
        </WdsStack>
      </div>
    </div>
    <div>
      <!-- Onboarding steps -->
      <div class="tw-bg-indigo-50/40 tw-pb-6 tw-pt-5 tw-rounded-bl-lg tw-rounded-br-lg">
        <WdsStack direction-column align-center>
          <WdsStack justify-space-between wrap class="tw-px-6">
            <div class="tw-w-full lg:tw-w-1/6 tw-mb-7 lg:tw-mb-0">
              <div class="m-b-15 line-height-1">
                <WdsIcon name="settings" :size="32" class="tw-text-gray-700" />
              </div>
              <WdsText>
                <div class="tw-font-semibold tw-pb-1">1. {{ $t('Complete your account') }}</div>
              </WdsText>
              <WdsText>
                <div class="tw-text-sm">
                  {{ $t('Add your business setup, banking & tax information.') }}
                </div>
              </WdsText>
            </div>

            <div class="tw-w-full lg:tw-w-1/6 tw-mb-7 lg:tw-mb-0">
              <div class="m-b-15 line-height-1">
                <WdsIcon name="task" :size="32" class="tw-text-gray-700" />
              </div>
              <WdsText>
                <div class="tw-font-semibold tw-pb-1">2. {{ $t('Get a hire offer') }}</div>
              </WdsText>
              <WdsText>
                <div class="tw-text-sm">
                  {{
                    $t('Once your client extends an offer, you can review & accept the contract.')
                  }}
                </div>
              </WdsText>
            </div>

            <div class="tw-w-full lg:tw-w-1/6 tw-mb-7 lg:tw-mb-0">
              <div class="m-b-15 line-height-1">
                <WdsIcon name="receipt" :size="32" class="tw-text-gray-700" />
              </div>
              <WdsText>
                <div class="tw-font-semibold tw-pb-1">
                  3. {{ $t('Request your first payment') }}
                </div>
              </WdsText>
              <WdsText>
                <div class="tw-text-sm">
                  {{
                    $t(
                      'After the request is approved by the client, they automatically receive an invoice.',
                    )
                  }}
                </div>
              </WdsText>
            </div>

            <div class="tw-w-full lg:tw-w-1/6">
              <div class="m-b-15 line-height-1">
                <WdsIcon name="piggy-bank-slot" :size="32" class="tw-text-gray-700" />
              </div>
              <WdsText>
                <div class="tw-font-semibold tw-pb-1">4. {{ $t('Get paid') }}</div>
              </WdsText>
              <WdsText>
                <div class="tw-text-sm">
                  {{ $t('Once the invoice is paid, you will receive a payout from Worksome.') }}
                </div>
              </WdsText>
            </div>

            <div class="tw-w-full lg:tw-w-1/6">
              <div class="m-b-15 line-height-1">
                <WdsIcon name="search-globe" :size="32" class="tw-text-gray-700" />
              </div>
              <WdsText>
                <div class="tw-pb-1">{{ $t('Want to get access to more job posts?') }}</div>
              </WdsText>
              <WdsButtonText :href="laravelNamedRoute('frontend.jobs.index')">
                {{ $t('Join marketplace') }}
              </WdsButtonText>
              <WdsText class="tw-pt-2">
                <div class="tw-text-sm">
                  {{ $t('*Requires a completed and reviewed public profile') }}
                </div>
              </WdsText>
            </div>
          </WdsStack>
        </WdsStack>
      </div>
    </div>
  </div>

  <!-- Collapsed version -->
  <div v-else-if="shouldShow" class="tw-bg-indigo-50 tw-rounded-lg tw-relative tw-overflow-hidden">
    <!-- Left decorative SVG -->
    <div class="tw-absolute -tw-left-2 -tw-bottom-6">
      <svg
        width="80"
        height="80"
        viewBox="0 0 187 187"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M93.5 0C117.333 0 136.654 41.8614 136.654 93.5C136.654 145.139 117.333 187 93.5 187C69.6668 187 50.3462 145.139 50.3462 93.5C50.3462 41.8614 69.6668 0 93.5 0Z"
          fill="#C3B6F9"
        />
        <path
          d="M187 93.5C187 117.333 145.139 136.654 93.5 136.654C41.8614 136.654 0 117.333 0 93.5C0 69.6668 41.8614 50.3462 93.5 50.3462C145.139 50.3462 187 69.6668 187 93.5Z"
          fill="#C3B6F9"
        />
      </svg>
    </div>

    <!-- Right decorative SVG -->
    <div class="tw-absolute -tw-right-5 -tw-top-9">
      <svg
        width="130"
        height="130"
        viewBox="0 0 314 261"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M8.59512 158.177L160.892 148.98L279.273 245.234L126.976 254.431L8.59512 158.177Z"
          fill="#FFC800"
        />
        <path
          d="M25.5531 105.452L177.85 96.254L296.231 192.508L143.934 201.706L25.5531 105.452Z"
          fill="#FFC800"
        />
        <path
          d="M42.5109 52.7258L194.808 43.5282L313.189 139.782L160.892 148.98L42.5109 52.7258Z"
          fill="#FFC800"
        />
      </svg>
    </div>

    <!-- Collapsed content -->
    <div class="tw-relative tw-z-10 tw-py-4 tw-px-6">
      <WdsStack justify-space-between align-center>
        <!-- Left: Welcome message & learn more button -->
        <WdsStack direction-row gap-md align-center>
          <WdsHeading type="title2" class="tw-text-indigo-500">
            {{ $t('Welcome to Worksome!') }}
          </WdsHeading>
          <WdsButtonGhost @click="toggleCollapsed">
            <WdsStack align-center gap-xs>
              <WdsText class="tw-text-sm">{{ $t('Learn more on how to get started') }}</WdsText>
              <WdsIcon name="chevron-down" :size="16" />
            </WdsStack>
          </WdsButtonGhost>
        </WdsStack>

        <!-- Right: CTA Button -->
        <div v-if="!isFreelancerOnboarded && !loadingAccount">
          <a :href="laravelNamedRoute(namedRoutes.frontend.user.profile.edit)">
            <WdsButton primary>
              <WdsStack align-center gap-sm>
                {{ $t('Complete your account') }}
                <WdsIcon name="arrow-right" :size="16" />
              </WdsStack>
            </WdsButton>
          </a>
        </div>
      </WdsStack>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  WdsButton,
  WdsStack,
  WdsText,
  WdsButtonGhost,
  WdsIcon,
  WdsHeading,
  WdsButtonText,
} from '@worksome/wds-components'
import { useLocalStorage } from '@vueuse/core'
import { laravelNamedRoute } from '@/utils/laravelNamedRoutes'
import namedRoutes from '@/utils/laravelNamedRoutes.json'
import { $t } from '@/composables/translation'
import { useFreelancerOnboardingCompliances } from '@/composables/useFreelancerOnboardingCompliances'

const isCollapsed = useLocalStorage('freelancerIntroductionCollapsed', false)

const { isFreelancerOnboarded, loadingAccount } = useFreelancerOnboardingCompliances()

const toggleCollapsed = () => {
  isCollapsed.value = !isCollapsed.value
}

const shouldShow = !currentUserHasBeenPaid
</script>
